import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Spinner } from '../../shared/ui/spinner';
import {
	Calendar,
	Pencil,
	Plus,
	Trash2,
	TrendingDown,
	TrendingUp,
	X,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
	endOfDay,
	endOfMonth,
	format,
	startOfDay,
	startOfMonth,
	subDays,
	subMonths,
} from 'date-fns';
import styles from './Income.module.css';
import { datePickerLocale } from '../../shared/i18n/date-picker-locale';
import { useAuth } from '../../context/AuthContext';
import { useOverviewCurrency } from '../../context/OverviewCurrencyContext';
import { useOverviewRefresh } from '../../context/OverviewRefreshContext';
import { getOverviewRequest } from '../../modules/dashbord/api/index';

import GlassMinus from '../../shared/assets/categories/GlassMinus.png';
import DatabaseDollar from '../../shared/assets/categories/DatabaseDollar.png';
import ReportMoney from '../../shared/assets/categories/ReportMoney.png';
import DeviceDesktopDollar from '../../shared/assets/categories/DeviceDesktopDollar.png';
import HomeDollar from '../../shared/assets/categories/HomeDollar.png';
import MoneybagPlus from '../../shared/assets/categories/MoneybagPlus.png';
import UserDollar from '../../shared/assets/categories/UserDollar.png';

import { Coins, Filter } from '../../shared/assets/icons';

import {
	formatDateOnlyToDDMMYYYY,
	sortTransactionsNewestFirst,
} from '../../shared/lib/date/date-utils';
import { sortIncomeCategories } from '../../shared/lib/category/sort-income-categories';
import AddRecordModal from '../../components/AddRecordModal/AddRecordModal';
import {
	getIncomesRequest,
	getRecurringIncomesRequest,
	incomesDeleteRequest,
	incomesGerCategoriesRequest,
	incomesGerSourcesRequest,
	recurringIncomesDeleteRequest,
} from './api/index';
import { buildListParams } from '../../shared/lib/list-params/build-list-params';

// Currency symbol helper function
const getCurrencySymbol = currency => {
	const symbols = {
		USD: '$',
		EUR: '€',
		AMD: '֏',
		RUB: '₽',
	};
	return symbols[currency] || currency;
};

/** "YYYY-MM" key used by the overview endpoint. */
const toMonthKey = date =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/**
 * Turns the overview response into one row per currency for the summary
 * card. Overview rows look like { currency, month: "YYYY-MM", income }.
 * The trend arrow compares this month against the previous one.
 */
const buildCurrencyTotals = overviewData => {
	const rows = Array.isArray(overviewData?.data) ? overviewData.data : [];
	const thisMonth = toMonthKey(new Date());
	const previousMonth = toMonthKey(subMonths(new Date(), 1));

	const byCurrency = new Map();
	rows.forEach(row => {
		if (row.month !== thisMonth && row.month !== previousMonth) return;

		const totals = byCurrency.get(row.currency) || { current: 0, previous: 0 };
		if (row.month === thisMonth) {
			totals.current += Number(row.income) || 0;
		} else {
			totals.previous += Number(row.income) || 0;
		}
		byCurrency.set(row.currency, totals);
	});

	return Array.from(byCurrency.entries())
		.filter(([, totals]) => totals.current > 0)
		.map(([currency, totals]) => ({
			currency,
			total: totals.current,
			previous: totals.previous,
			trend: totals.current < totals.previous ? 'down' : 'up',
		}))
		.sort((a, b) => b.total - a.total);
};

const Income = () => {
	const intl = useIntl();
	const { userInfo } = useAuth();
	const { currency: overviewCurrency } = useOverviewCurrency();
	const [activeFilters, setActiveFilters] = useState(['All']);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [selectedCategories, setSelectedCategories] = useState(['All']);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [dateRange, setDateRange] = useState({
		from: null,
		to: null,
	});
	/** Server-side list filters (updated on Apply / reset / chip remove, not on modal draft toggles). */
	const [committedCategoryNames, setCommittedCategoryNames] = useState(['All']);
	const [committedDateRange, setCommittedDateRange] = useState({
		from: null,
		to: null,
	});
	const [listFetchKey, setListFetchKey] = useState(0);
	const [categories, setCategories] = useState([]);
	const [sources, setSources] = useState([]);
	const [incomes, setIncomes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [hiddenChartCategories, setHiddenChartCategories] = useState([]);
	const [editingIncome, setEditingIncome] = useState(null);
	const [deleteConfirmIncome, setDeleteConfirmIncome] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [viewMode, setViewMode] = useState('regular'); // "regular" | "recurring"
	const [recurringIncomes, setRecurringIncomes] = useState([]);
	const [editingRecurringIncome, setEditingRecurringIncome] = useState(null);
	const [deleteConfirmRecurringIncome, setDeleteConfirmRecurringIncome] =
		useState(null);
	const [deleteRecurringLoading, setDeleteRecurringLoading] = useState(false);
	/** Currency picked in the sidebar-header – drives the chart and its tooltips. */
	const [userCurrency, setUserCurrency] = useState('USD');
	/** Current-month income per currency: [{ currency, total, trend }]. */
	const [currencyTotals, setCurrencyTotals] = useState([]);
	const [monthComparison, setMonthComparison] = useState({
		percentage: null,
		trend: null,
		message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
	});

	const refreshData = useCallback(() => {
		setRefreshTrigger(prev => prev + 1);
	}, []);

	const { setRefreshCallback } = useOverviewRefresh();

	useEffect(() => {
		setRefreshCallback(refreshData);
		return () => setRefreshCallback(null);
	}, [setRefreshCallback, refreshData]);

	const handleEditIncome = income => {
		setEditingIncome(income);
		setIsModalOpen(true);
	};

	const handleDeleteIncome = incomeId => {
		const incomeToDelete = incomes.find(inc => inc.id === incomeId);
		if (incomeToDelete) {
			setDeleteConfirmIncome(incomeToDelete);
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteConfirmIncome) return;

		setDeleteLoading(true);
		try {
			await incomesDeleteRequest(deleteConfirmIncome.id);
			setIncomes(incomes.filter(inc => inc.id !== deleteConfirmIncome.id));
			setDeleteConfirmIncome(null);
			refreshData();
		} catch (err) {
			console.error('Error deleting income:', err);
			alert(err.message || 'Failed to delete income');
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmIncome(null);
	};

	const handleEditRecurringIncome = recurringIncome => {
		setEditingRecurringIncome(recurringIncome);
		setIsModalOpen(true);
	};

	const handleDeleteRecurringIncome = recurringIncomeId => {
		const recurringIncomeToDelete = recurringIncomes.find(
			inc => inc.id === recurringIncomeId,
		);
		if (recurringIncomeToDelete) {
			setDeleteConfirmRecurringIncome(recurringIncomeToDelete);
		}
	};

	const handleConfirmDeleteRecurring = async () => {
		if (!deleteConfirmRecurringIncome) return;

		setDeleteRecurringLoading(true);
		try {
			await recurringIncomesDeleteRequest(deleteConfirmRecurringIncome.id);
			setRecurringIncomes(
				recurringIncomes.filter(
					inc => inc.id !== deleteConfirmRecurringIncome.id,
				),
			);
			setDeleteConfirmRecurringIncome(null);
			refreshData();
		} catch (err) {
			console.error('Error deleting recurring income:', err);
			alert(err.message || 'Failed to delete recurring income');
		} finally {
			setDeleteRecurringLoading(false);
		}
	};

	const handleCancelDeleteRecurring = () => {
		setDeleteConfirmRecurringIncome(null);
	};

	// Icon mapping function
	const getCategoryIcon = categoryName => {
		const iconMap = {
			business: DatabaseDollar,
			salary: MoneybagPlus,
			freelance: DeviceDesktopDollar,
			rental: HomeDollar,
			investment: ReportMoney,
			other: UserDollar,
			education: UserDollar,
			entertainment: UserDollar,
			food: UserDollar,
			gifts: UserDollar,
			healthcare: UserDollar,
			housing: UserDollar,
			insurance: UserDollar,
			'personal care': UserDollar,
			shopping: UserDollar,
			taxes: UserDollar,
			transportation: UserDollar,
			travel: UserDollar,
			utilities: UserDollar,
		};

		return iconMap[categoryName?.toLowerCase()] || UserDollar;
	};

	const calculateMonthComparison = (current, previous) => {
		if (!previous) {
			return {
				percentage: null,
				trend: null,
				message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
			};
		}

		const percentage = ((current - previous) / previous) * 100;
		const absPercentage = Math.abs(percentage).toFixed(1);

		return {
			percentage: absPercentage,
			trend: percentage >= 0 ? 'up' : 'down',
			message: `${absPercentage}% ${intl.formatMessage({ id: 'FinPathDashboardCartID11' })}`,
		};
	};

	// Summary card: current-month income for every currency the user has.
	useEffect(() => {
		const fetchOverviewData = async () => {
			try {
				const selectedCurrency =
					overviewCurrency || userInfo?.currency_preference || 'USD';
				setUserCurrency(selectedCurrency);
				const overviewResponse = await getOverviewRequest();
				const totals = buildCurrencyTotals(overviewResponse);
				setCurrencyTotals(totals);

				const row =
					totals.find(r => r.currency === selectedCurrency) ?? totals[0];
				setMonthComparison(
					row
						? calculateMonthComparison(row.total, row.previous)
						: {
								percentage: null,
								trend: null,
								message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
							},
				);
			} catch (error) {
				console.error('Failed to fetch overview data:', error);
				setCurrencyTotals([]);
				setMonthComparison({
					percentage: null,
					trend: null,
					message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
				});
			}
		};

		fetchOverviewData();
	}, [overviewCurrency, userInfo, refreshTrigger]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);

				const categoriesResponse = await incomesGerCategoriesRequest();
				const categoriesData =
					categoriesResponse?.categories ||
					categoriesResponse?.data ||
					categoriesResponse ||
					[];

				const incomeListParams = buildListParams(
					{ skip: 0, limit: 1000 },
					categoriesData,
					committedCategoryNames,
					committedDateRange,
				);

				const [sourcesResponse, incomesResponse, recurringIncomesResponse] =
					await Promise.all([
						incomesGerSourcesRequest(),
						getIncomesRequest(incomeListParams),
						getRecurringIncomesRequest(),
					]);

				const sourcesData =
					sourcesResponse?.sources ||
					sourcesResponse?.data ||
					sourcesResponse ||
					[];

				const incomesData =
					incomesResponse?.incomes ||
					incomesResponse?.data ||
					incomesResponse ||
					[];

				const recurringIncomesData =
					recurringIncomesResponse?.recurring_incomes ||
					recurringIncomesResponse?.data ||
					recurringIncomesResponse ||
					[];

				setCategories(categoriesData);
				setSources(sourcesData);
				setIncomes(incomesData);
				setRecurringIncomes(recurringIncomesData);
			} catch (error) {
				console.error('Failed to load data:', error);
				setCategories([]);
				setSources([]);
				setIncomes([]);
				setRecurringIncomes([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		// Committed filters are read on purpose without being dependencies: the
		// list refetches when the user applies them (listFetchKey), not while the
		// filter modal is still being edited.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTrigger, listFetchKey]);

	const getSourceName = sourceId => {
		if (!sourceId) return '—';

		const source = sources.find(s => s.id === sourceId);
		return source?.name || sourceId;
	};

	// Use appropriate data source based on view mode; only amounts in sidebar-header currency for chart/sum
	const dataSource = viewMode === 'recurring' ? recurringIncomes : incomes;
	const selectedCurrency =
		overviewCurrency || userInfo?.currency_preference || 'USD';
	const dataSourceInCurrency = dataSource.filter(
		income => (income.currency || 'USD') === selectedCurrency,
	);

	const expenseData = categories
		.map(category => {
			const categoryIncomes = dataSourceInCurrency.filter(
				income =>
					income.category_id === category.id ||
					income.category === category.name,
			);

			const totalAmount = categoryIncomes.reduce(
				(sum, income) => sum + (Number(income.amount) || 0),
				0,
			);

			if (totalAmount > 0) {
				const colorLookupName = category.original_name || category.name;
				return {
					id: category.id || category.name,
					name: category.name,
					value: totalAmount,
					displayValue: totalAmount,
					icon: getCategoryIcon(category.original_name || category.name),
					color: getColorForCategory(colorLookupName),
				};
			}
			return null;
		})
		.filter(Boolean);

	// Use only real data – no placeholder when there are no incomes
	const finalExpenseData = expenseData;

	// Prepare transactions from API data only
	const transactionsFromAPI = incomes
		.map(income => {
			const category = categories.find(
				cat => cat.id === income.category_id || cat.name === income.category,
			);

			const amount = Number(income.amount) || 0;
			if (amount > 0) {
				return {
					id: income.id || Math.random(),
					category: category?.name || income.category || 'Other',
					source: getSourceName(income.source_id),
					date: formatDateOnlyToDDMMYYYY(income.date),
					transactionDate: income.date,
					createdAt: income.created_at,
					amount: amount,
					icon: getCategoryIcon(
						category?.original_name || category?.name || income.category,
					),
					currency: income.currency || '$',
				};
			}
			return null;
		})
		.filter(Boolean);

	// Prepare recurring transactions
	const recurringTransactionsFromAPI = recurringIncomes
		.map(recurringIncome => {
			const category = categories.find(
				cat => cat.id === recurringIncome.category_id,
			);

			const amount = Number(recurringIncome.amount) || 0;
			if (amount > 0) {
				const recurringPeriod =
					recurringIncome.recurring_period === 'weekly'
						? intl.formatMessage({ id: 'FrequencyID1' })
						: recurringIncome.recurring_period === 'monthly'
							? intl.formatMessage({ id: 'FrequencyD2' })
							: recurringIncome.recurring_period ||
								intl.formatMessage({ id: 'FrequencyD2' });
				return {
					id: recurringIncome.id || Math.random(),
					category: category?.name || 'Other',
					source: getSourceName(recurringIncome.source_id),
					date: formatDateOnlyToDDMMYYYY(recurringIncome.start_date),
					transactionDate: recurringIncome.start_date,
					createdAt: recurringIncome.created_at,
					frequency: recurringPeriod,
					amount: amount,
					icon: getCategoryIcon(category?.original_name || category?.name),
					currency: recurringIncome.currency || '$',
					isRecurring: true,
				};
			}
			return null;
		})
		.filter(Boolean);

	const sortedTransactions = sortTransactionsNewestFirst(transactionsFromAPI);
	const sortedRecurringTransactions = sortTransactionsNewestFirst(
		recurringTransactionsFromAPI,
	);

	// Ensure chart data has proper numeric values
	const validatedChartData = finalExpenseData.map(item => ({
		...item,
		value: Number(item.value) || 0,
		displayValue: Number(item.displayValue) || Number(item.value) || 0,
	}));

	// Handle legend click to hide/show chart segment
	const handleLegendClick = categoryName => {
		setHiddenChartCategories(prev => {
			if (prev.includes(categoryName)) {
				return prev.filter(cat => cat !== categoryName);
			} else {
				return [...prev, categoryName];
			}
		});
	};

	// TODO Code Duplicate
	const handleClearFilters = () => {
		setSelectedCategories(['All']);
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setHiddenChartCategories([]);
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setListFetchKey(k => k + 1);
	};

	// TODO Code Duplicate
	// Filter chart data based on hidden categories
	const getFilteredChartData = () => {
		const baseFilteredData = committedCategoryNames.includes('All')
			? validatedChartData
			: validatedChartData.filter(item =>
					committedCategoryNames.includes(item.name),
				);

		// Filter out hidden categories
		return baseFilteredData.filter(
			item => !hiddenChartCategories.includes(item.name),
		);
	};

	const currentChartData = getFilteredChartData();

	const removeFilter = filter => {
		if (filter === 'All') {
			setSelectedCategories(['All']);
			setDateRange({ from: null, to: null });
			setActiveFilters(['All']);
			setHiddenChartCategories([]);
			setCommittedCategoryNames(['All']);
			setCommittedDateRange({ from: null, to: null });
			setListFetchKey(k => k + 1);
			return;
		}

		const newActiveFilters = activeFilters.filter(f => f !== filter);
		let nextCategories = [...selectedCategories];
		let nextDateRange = { ...dateRange };

		const allCategoryNames = categories.map(cat => cat.name);
		if (allCategoryNames.includes(filter)) {
			nextCategories = selectedCategories.filter(cat => cat !== filter);
			if (nextCategories.length === 0) {
				nextCategories = ['All'];
			}
		}

		if (
			filter.includes('→') ||
			/^\d{2}\.\d{2}\.\d{2}$/.test(String(filter).trim())
		) {
			nextDateRange = { from: null, to: null };
		}

		setSelectedCategories(nextCategories);
		setDateRange(nextDateRange);
		setActiveFilters(newActiveFilters.length > 0 ? newActiveFilters : ['All']);
		setCommittedCategoryNames(nextCategories);
		setCommittedDateRange(nextDateRange);
		setListFetchKey(k => k + 1);
	};

	const handleCategoryToggle = category => {
		if (category === 'All') {
			if (selectedCategories.includes('All')) {
				setSelectedCategories([]);
				setHiddenChartCategories([]);
			} else {
				const allCategoryNames = categories.map(cat => cat.name);
				setSelectedCategories(['All', ...allCategoryNames]);
			}
		} else {
			setSelectedCategories(prev => {
				const newCategories = prev.includes(category)
					? prev.filter(cat => cat !== category && cat !== 'All')
					: [...prev.filter(cat => cat !== 'All'), category];

				const allCategoryNames = categories.map(cat => cat.name);
				const allSelected = allCategoryNames.every(cat =>
					newCategories.includes(cat),
				);

				if (allSelected) {
					return ['All', ...allCategoryNames];
				}

				return newCategories;
			});
		}
	};

	// TODO Code Duplicate
	const handleApplyFilters = () => {
		const newFilters = [];

		// Add category filters
		if (selectedCategories.includes('All')) {
			newFilters.push('All');
		} else if (selectedCategories.length > 0) {
			const selectedCats = selectedCategories.filter(cat => cat !== 'All');
			newFilters.push(...selectedCats);
		}

		// Add date range filter
		if (dateRange.from && dateRange.to) {
			const fromStr = format(dateRange.from, 'dd.MM.yy');
			const toStr = format(dateRange.to, 'dd.MM.yy');
			if (fromStr === toStr) {
				newFilters.push(fromStr);
			} else {
				newFilters.push(`${fromStr} → ${toStr}`);
			}
		}

		setActiveFilters(newFilters.length > 0 ? newFilters : ['All']);
		setCommittedCategoryNames([...selectedCategories]);
		setCommittedDateRange({
			from: dateRange.from,
			to: dateRange.to,
		});
		setListFetchKey(k => k + 1);
		setShowFilterModal(false);
	};

	const handleResetFilters = () => {
		setSelectedCategories(['All']);
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setHiddenChartCategories([]);
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setListFetchKey(k => k + 1);
	};

	const handleDatePreset = preset => {
		const today = startOfDay(new Date());

		let fromDate = null;
		let toDate = today;

		switch (preset) {
			case 'today':
				fromDate = today;
				toDate = today;
				break;
			case 'last7days':
				fromDate = subDays(today, 7);
				break;
			case 'last30days':
				fromDate = subDays(today, 30);
				break;
			case 'thisMonth':
				fromDate = startOfMonth(today);
				toDate = endOfMonth(today);
				break;
			case 'lastMonth':
				const lastMonth = subMonths(today, 1);
				fromDate = startOfMonth(lastMonth);
				toDate = endOfMonth(lastMonth);
				break;
			default:
				break;
		}

		setDateRange({ from: fromDate, to: toDate });
	};

	// TODO Code Duplicate
	// Get the appropriate transactions list based on view mode
	const transactionsToFilter =
		viewMode === 'recurring' ? sortedRecurringTransactions : sortedTransactions;

	// Regular rows are server-filtered; recurring still uses client category + date filters.
	const filteredTransactions = transactionsToFilter.filter(transaction => {
		if (viewMode !== 'recurring') {
			return true;
		}

		if (
			!selectedCategories.includes('All') &&
			!selectedCategories.includes(transaction.category)
		) {
			return false;
		}

		if (dateRange.from || dateRange.to) {
			const parts = transaction.date.split('.');
			const transactionDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);

			const fromDate = dateRange.from
				? startOfDay(new Date(dateRange.from))
				: null;
			const toDate = dateRange.to ? endOfDay(new Date(dateRange.to)) : null;

			const dateMatch =
				(!fromDate || transactionDate >= fromDate) &&
				(!toDate || transactionDate <= toDate);

			if (!dateMatch) {
				return false;
			}
		}

		return true;
	});

	const categoryData = [
		{ name: 'All', icon: null },
		...sortIncomeCategories(categories).map(category => ({
			name: category.name,
			icon: getCategoryIcon(category.original_name || category.name),
		})),
	];

	/** Column widths shared by the table sidebar-header and its rows. */
	const tableColumns =
		viewMode === 'recurring' ? '2fr 1fr 1fr 1fr 120px' : '2fr 1fr 1fr 120px';

	/** Edit + delete buttons, shown on every table row and mobile card. */
	const renderRowActions = transaction => {
		const record =
			viewMode === 'recurring'
				? recurringIncomes.find(inc => inc.id === transaction.id)
				: incomes.find(inc => inc.id === transaction.id);

		return (
			<>
				<button
					type='button'
					className={styles.iconButton}
					disabled={!record}
					title={intl.formatMessage({ id: 'IncomeID31' })}
					aria-label={intl.formatMessage({ id: 'IncomeID31' })}
					onClick={() =>
						viewMode === 'recurring'
							? handleEditRecurringIncome(record)
							: handleEditIncome(record)
					}
				>
					<Pencil size={18} />
				</button>
				<button
					type='button'
					className={`${styles.iconButton} ${styles.deleteButton}`}
					title={intl.formatMessage({ id: 'IncomeID32' })}
					aria-label={intl.formatMessage({ id: 'IncomeID32' })}
					onClick={() =>
						viewMode === 'recurring'
							? handleDeleteRecurringIncome(transaction.id)
							: handleDeleteIncome(transaction.id)
					}
				>
					<Trash2 size={18} />
				</button>
			</>
		);
	};

	// TODO Code Duplicate
	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			return (
				<div className={styles.customTooltip}>
					<p className={styles.tooltipLabel}>{data.name}</p>
					<p className={styles.tooltipValue}>
						{(data.displayValue || data.value).toLocaleString()}
						{getCurrencySymbol(userCurrency)}
					</p>
				</div>
			);
		}
		return null;
	};

	if (loading) {
		return <Spinner />;
	}

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.topSection}>
					<div className={styles.summaryCard}>
						<h2 className={styles.summaryTitle}>
							{intl.formatMessage({ id: 'IncomeID5' })}
						</h2>
						<div className={styles.coinIcon}>
							<Coins />
						</div>
						<div className={styles.summaryAmounts}>
							{(currencyTotals.length > 0
								? currencyTotals
								: [{ currency: userCurrency, total: 0, trend: null }]
							).map(({ currency, total, trend }) => (
								<div key={currency} className={styles.summaryAmountRow}>
									<span className={styles.summaryAmountValue}>
										{total.toLocaleString()} {getCurrencySymbol(currency)}
									</span>
									{trend === 'down' ? (
										<TrendingDown className={styles.trendIconDown} size={18} />
									) : trend === 'up' ? (
										<TrendingUp className={styles.trendIcon} size={18} />
									) : null}
								</div>
							))}
						</div>
						<div className={styles.summaryChange}>
							<span className={styles.paragrap}>{monthComparison.message}</span>
						</div>
					</div>

					<div className={styles.chartContainer}>
						{currentChartData.length > 0 ? (
							<div className={styles.chartWrapper}>
								<ResponsiveContainer width='100%' height={280}>
									<PieChart>
										<Pie
											data={currentChartData}
											cx='50%'
											cy='50%'
											innerRadius={0}
											outerRadius='95%'
											paddingAngle={1}
											dataKey='value'
											minAngle={5}
											isAnimationActive={false}
										>
											{currentChartData.map((entry, index) => (
												<Cell key={`cell-${index}`} fill={entry.color} />
											))}
										</Pie>
										<Tooltip
											content={<CustomTooltip />}
											animationDuration={0}
										/>
									</PieChart>
								</ResponsiveContainer>

								<div className={styles.legendContainer}>
									{validatedChartData
										.filter(item =>
											selectedCategories.includes('All')
												? true
												: selectedCategories.includes(item.name),
										)
										.map((item, index) => {
											const isHidden = hiddenChartCategories.includes(
												item.name,
											);
											return (
												<div
													key={index}
													className={styles.legendRow}
													onClick={() => handleLegendClick(item.name)}
													style={{ cursor: 'pointer' }}
												>
													<div
														className={styles.legendColor}
														style={{
															backgroundColor: isHidden
																? '#e0e0e0'
																: item.color,
															opacity: isHidden ? 0.5 : 1,
															position: 'relative',
														}}
													>
														{isHidden && (
															<div className={styles.hiddenIndicator}>×</div>
														)}
													</div>
													<span
														className={`${styles.legendName} ${
															isHidden ? styles.legendNameHidden : ''
														}`}
													>
														{item.name}
													</span>
												</div>
											);
										})}
								</div>
							</div>
						) : (
							/* Nothing recorded yet – plain empty circle, like the overview page. */
							<div className={styles.noDataMessage} />
						)}
					</div>
				</div>

				<div className={styles.filtersSection}>
					<div className={styles.filterControls}>
						<button
							className={`${styles.viewModeToggle} ${
								viewMode === 'regular' ? styles.viewModeActive : ''
							}`}
							onClick={() => setViewMode('regular')}
						>
							{intl.formatMessage({ id: 'IncomeID8' })}
						</button>
						<button
							className={`${styles.viewModeToggle} ${
								viewMode === 'recurring' ? styles.viewModeActive : ''
							}`}
							onClick={() => setViewMode('recurring')}
						>
							{intl.formatMessage({ id: 'IncomeID9' })}
						</button>
						<button
							className={styles.filterButton}
							onClick={() => setShowFilterModal(true)}
						>
							<Filter />
							<span>{intl.formatMessage({ id: 'IncomeID10' })}</span>
						</button>
						{activeFilters
							.filter(filter => filter !== 'All')
							.map(filter => (
								<button key={filter} className={styles.filterTag}>
									<span>{filter}</span>
									<X size={16} onClick={() => removeFilter(filter)} />
								</button>
							))}
					</div>
					<button
						className={styles.addButton}
						onClick={() => setIsModalOpen(true)}
					>
						<span>{intl.formatMessage({ id: 'IncomeID11' })}</span>
						<Plus size={20} />
					</button>
				</div>

				{showFilterModal && (
					<div className={styles.modalOverlay}>
						<div className={styles.modalContent}>
							<div className={styles.modalHeader}>
								<h3>{intl.formatMessage({ id: 'IncomeID12' })}</h3>
								<button
									className={styles.closeButton}
									onClick={() => setShowFilterModal(false)}
								>
									<X size={20} />
								</button>
							</div>

							<div className={styles.filterSection}>
								<div className={styles.dateSelector}>
									<h4 className={styles.sectionTitle}>
										{intl.formatMessage({ id: 'IncomeID13' })}
									</h4>

									<div className={styles.dateInputs}>
										<div className={styles.dateInputGroup}>
											<label>{intl.formatMessage({ id: 'IncomeID14' })}</label>
											{/* TODO Code Duplicate */}
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.from}
													onChange={date => {
														if (dateRange.to && date > dateRange.to) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																from: date,
															}));
														}
													}}
													selectsStart
													startDate={dateRange.from}
													endDate={dateRange.to}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-start'
													calendarStartDay={1}
													showMonthYearDropdown
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
										<div className={styles.dateInputGroup}>
											<label>{intl.formatMessage({ id: 'IncomeID15' })}</label>
											{/* TODO Code Duplicate */}
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.to}
													onChange={date => {
														if (dateRange.from && date < dateRange.from) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																to: date,
															}));
														}
													}}
													selectsEnd
													startDate={dateRange.from}
													endDate={dateRange.to}
													minDate={dateRange.from}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-end'
													calendarStartDay={1}
													showMonthYearDropdown
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
									</div>

									<div className={styles.datePresetsSection}>
										<div className={styles.datePresets}>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last30days')}
											>
												{intl.formatMessage({ id: 'IncomeID16' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last7days')}
											>
												{intl.formatMessage({ id: 'IncomeID17' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('today')}
											>
												{intl.formatMessage({ id: 'IncomeID18' })}
											</button>
										</div>
									</div>
								</div>
							</div>

							<div className={styles.filterSection}>
								<h4 className={styles.sectionTitle}>
									{intl.formatMessage({ id: 'IncomeID19' })}
								</h4>
								<div className={styles.categoryList}>
									{categoryData.map(category => {
										const isChecked =
											category.name === 'All'
												? selectedCategories.includes('All')
												: selectedCategories.includes(category.name) ||
													selectedCategories.includes('All');

										return (
											<label
												key={category.name}
												className={styles.categoryItem}
											>
												{category.icon && (
													<img src={category.icon} alt={category.name} />
												)}
												{!category.icon && (
													<div className={styles.iconPlaceholder}></div>
												)}
												<span className={styles.categoryLabel}>
													{category.name}
												</span>

												<div className={styles.checkboxWrapper}>
													<input
														type='checkbox'
														checked={isChecked}
														onChange={() => handleCategoryToggle(category.name)}
														className={styles.checkbox}
													/>
													<span className={styles.checkmark}></span>
												</div>
											</label>
										);
									})}
								</div>
							</div>

							<div className={styles.modalActions}>
								<button
									className={styles.resetButton}
									onClick={handleResetFilters}
								>
									{intl.formatMessage({ id: 'IncomeID20' })}
								</button>
								<button
									className={styles.applyButton}
									onClick={handleApplyFilters}
								>
									{intl.formatMessage({ id: 'IncomeID21' })}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Mobile: card list */}
				<div className={styles.cardsContainer}>
					{filteredTransactions.length > 0 ? (
						filteredTransactions.map((transaction, index) => (
							<div
								key={`card-${transaction.id}-${index}`}
								className={styles.incomeCard}
							>
								<div className={styles.cardTopRow}>
									<span className={styles.cardDate}>{transaction.date}</span>
									<div className={styles.actionsCell}>
										{renderRowActions(transaction)}
									</div>
								</div>
								<div className={styles.cardMainRow}>
									<div className={styles.cardCategory}>
										<img
											src={transaction.icon}
											alt={transaction.category}
											className={styles.cardCategoryIcon}
										/>
										<span>{transaction.category}</span>
									</div>
									<span className={styles.cardAmount}>
										{transaction.amount} {transaction.currency}
									</span>
								</div>
							</div>
						))
					) : (
						<div className={styles.emptyTableState}>
							<h2 className={styles.emptyTableTitle}>
								{intl.formatMessage({ id: 'IncomeID33' })}
							</h2>
							<img
								className={styles.emptyTableImage}
								src={GlassMinus}
								alt='GlassMinus'
							/>
							<p className={styles.emptyTableMessage}>
								{intl.formatMessage({ id: 'IncomeID34' })}
								<br />
								{intl.formatMessage({ id: 'IncomeID35' })}
							</p>
							<button
								className={styles.clearFiltersButton}
								onClick={handleClearFilters}
							>
								{intl.formatMessage({ id: 'IncomeID36' })}
							</button>
						</div>
					)}
				</div>

				{/* Desktop: table */}
				<div className={styles.tableContainer}>
					<div
						className={styles.tableHeader}
						style={{ gridTemplateColumns: tableColumns }}
					>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'IncomeID24' })}
						</div>
						{viewMode === 'recurring' && (
							<div className={styles.tableHeaderCell}>
								{intl.formatMessage({ id: 'IncomeID26' })}
							</div>
						)}
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({
								id: viewMode === 'recurring' ? 'IncomeID28' : 'IncomeID27',
							})}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'IncomeID29' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'IncomeID30' })}
						</div>
					</div>

					<div className={styles.tableBody}>
						{filteredTransactions.length > 0 ? (
							filteredTransactions.map(transaction => (
								<div
									key={transaction.id}
									className={styles.tableRow}
									style={{ gridTemplateColumns: tableColumns }}
								>
									<div className={styles.tableCell}>
										<img
											src={transaction.icon}
											alt=''
											className={styles.categoryIcon}
										/>
										<span className={styles.cellText}>
											{transaction.category}
										</span>
									</div>
									{viewMode === 'recurring' && (
										<div className={styles.tableCell}>
											{transaction.frequency}
										</div>
									)}
									<div className={styles.tableCell}>{transaction.date}</div>
									<div className={`${styles.tableCell} ${styles.amountCell}`}>
										{transaction.amount.toLocaleString()}{' '}
										{getCurrencySymbol(transaction.currency)}
									</div>
									<div className={`${styles.tableCell} ${styles.actionsCell}`}>
										{renderRowActions(transaction)}
									</div>
								</div>
							))
						) : (
							<div className={styles.emptyTableState}>
								<h2 className={styles.emptyTableTitle}>
									{intl.formatMessage({ id: 'IncomeID33' })}
								</h2>
								<img
									className={styles.emptyTableImage}
									src={GlassMinus}
									alt=''
								/>
								<p className={styles.emptyTableMessage}>
									{intl.formatMessage({ id: 'IncomeID34' })}
									<br />
									{intl.formatMessage({ id: 'IncomeID35' })}
								</p>
								<button
									className={styles.clearFiltersButton}
									onClick={handleClearFilters}
								>
									{intl.formatMessage({ id: 'IncomeID36' })}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<AddRecordModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditingIncome(null);
					setEditingRecurringIncome(null);
				}}
				onSuccess={() => refreshData()}
				defaultTab='income'
				editingIncome={editingIncome || editingRecurringIncome}
			/>
			{deleteConfirmIncome && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<div
							className={styles.modalHeader}
							style={{
								justifyContent: 'center',
								borderBottom: 'none',
								paddingBottom: 0,
								marginBottom: '24px',
							}}
						>
							<h3 style={{ textAlign: 'center', width: '100%' }}>
								{intl.formatMessage({ id: 'IncomeID39' })}
							</h3>
						</div>
						<p
							style={{
								textAlign: 'center',
								marginBottom: '24px',
								marginTop: '0',
							}}
						>
							{intl.formatMessage({ id: 'IncomeID40' })}
						</p>
						<div
							className={styles.modalActions}
							style={{ borderTop: 'none', paddingTop: 0 }}
						>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDelete}
								disabled={deleteLoading}
							>
								{deleteLoading
									? intl.formatMessage({ id: 'IncomeID41' })
									: intl.formatMessage({ id: 'IncomeID42' })}
							</button>
							<button
								className={styles.cancelConfirmButton}
								onClick={handleCancelDelete}
								disabled={deleteLoading}
							>
								{intl.formatMessage({ id: 'IncomeID43' })}
							</button>
						</div>
					</div>
				</div>
			)}
			{deleteConfirmRecurringIncome && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<div
							className={styles.modalHeader}
							style={{
								justifyContent: 'center',
								borderBottom: 'none',
								paddingBottom: 0,
								marginBottom: '24px',
							}}
						>
							<h3 style={{ textAlign: 'center', width: '100%' }}>
								{intl.formatMessage({ id: 'IncomeID44' })}
							</h3>
						</div>
						<p
							style={{
								textAlign: 'center',
								marginBottom: '24px',
								marginTop: '0',
							}}
						>
							{intl.formatMessage({ id: 'IncomeID45' })}
						</p>
						<div
							className={styles.modalActions}
							style={{ borderTop: 'none', paddingTop: 0 }}
						>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDeleteRecurring}
								disabled={deleteRecurringLoading}
							>
								{deleteRecurringLoading
									? intl.formatMessage({ id: 'IncomeID41' })
									: intl.formatMessage({ id: 'IncomeID42' })}
							</button>
							<button
								className={styles.cancelConfirmButton}
								onClick={handleCancelDeleteRecurring}
								disabled={deleteRecurringLoading}
							>
								{intl.formatMessage({ id: 'IncomeID46' })}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Income;

function getColorForCategory(categoryName) {
	const colors = {
		Business: '#2d7a65',
		Salary: '#6fd4c8',
		Freelance: '#4db8a8',
		Rental: '#a8e6d9',
		Investment: '#8ddcc9',
		Other: '#c4ede4',
		Education: '#d32f2f',
		Entertainment: '#4db6ac',
		'Food & Dining': '#c62828',
		'Gifts & Donations': '#c5cae9',
		Healthcare: '#b39ddb',
		Housing: '#ef5350',
		Insurance: '#e57373',
		'Personal Care': '#81c784',
		Shopping: '#9fa8da',
		Taxes: '#7986cb',
		Transportation: '#5c6bc0',
		Travel: '#9575cd',
		Utilities: '#7e57c2',
	};

	return colors[categoryName] || '#cccccc';
}
